import React, { useState, useRef, useEffect } from 'react'

export default function DrillDownCourseSelect({ courses, courseCategories, selectedCourseId, onSelect, colors, translations }) {
  const [open, setOpen] = useState(false)
  const [drillGroup, setDrillGroup] = useState(null)
  const ref = useRef(null)

  // Build category groups
  const courseGroups = React.useMemo(() => {
    const activeCategories = (courseCategories || []).filter(cat => cat.courseIds?.length > 0)
    if (activeCategories.length === 0) return null

    const categorizedIds = new Set(activeCategories.flatMap(cat => cat.courseIds))
    const uncategorized = courses?.filter(c => !categorizedIds.has(c.id)) || []

    const groups = activeCategories
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999))
      .map(cat => ({
        id: cat.id,
        label: cat.name,
        courses: cat.courseIds.map(id => courses?.find(c => c.id === id)).filter(Boolean)
      }))
      .filter(g => g.courses.length > 0)

    if (uncategorized.length > 0) {
      groups.push({
        id: 'other',
        label: translations?.otherCategory || '其他',
        courses: uncategorized
      })
    }

    return groups
  }, [courseCategories, courses, translations?.otherCategory])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
        setDrillGroup(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selectedCourse = courses?.find(c => c.id === selectedCourseId)
  const primary = colors?.primary || '#3366FF'

  const handleSelect = (course) => {
    onSelect(course.id)
    setOpen(false)
    setDrillGroup(null)
  }

  // No categories → plain select
  if (!courseGroups) {
    return (
      <select
        value={selectedCourseId || ''}
        onChange={(e) => onSelect(Number(e.target.value))}
        className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:border-transparent"
        style={{ '--tw-ring-color': primary }}
      >
        {courses?.map((course) => (
          <option key={course.id} value={course.id}>
            {course.name} ({course.durationMinutes}{translations?.min || 'min'})
          </option>
        ))}
      </select>
    )
  }

  // Drill-down dropdown
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen(!open); setDrillGroup(null) }}
        className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white text-left flex items-center justify-between cursor-pointer hover:border-gray-300 transition-colors"
      >
        <span className="text-sm truncate">
          {selectedCourse?.name || translations?.selectCourse || 'Select Course'}
          {selectedCourse && <span className="text-gray-400 ml-1">({selectedCourse.durationMinutes}{translations?.min || 'min'})</span>}
        </span>
        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {drillGroup ? (
            <>
              <button
                type="button"
                onClick={() => setDrillGroup(null)}
                className="w-full px-4 py-2.5 text-left text-xs font-medium flex items-center gap-1.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                style={{ color: primary }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {drillGroup.label}
              </button>
              <div className="max-h-48 overflow-y-auto">
                {drillGroup.courses.map(course => (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => handleSelect(course)}
                    className={`w-full px-4 py-2.5 text-left text-sm cursor-pointer transition-colors ${
                      selectedCourseId === course.id ? 'font-medium' : 'hover:bg-gray-50'
                    }`}
                    style={selectedCourseId === course.id ? { backgroundColor: '#E6F0FF', color: primary } : undefined}
                  >
                    {course.name}
                    <span className="text-xs text-gray-400 ml-1">({course.durationMinutes}{translations?.min || 'min'})</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {courseGroups.map(group => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => setDrillGroup(group)}
                  className="w-full px-4 py-2.5 text-left text-sm cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="font-medium">{group.label}</span>
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    {group.courses.length}
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
